"use client";

import React from "react";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { Provider } from "react-redux";
import { persistStore, persistReducer } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";

/* -------------------------------------------------------------------------- */
/* 🧩 Import your remaining slices */
/* -------------------------------------------------------------------------- */
import languageReducer from "./slices/languageSlice";
import darkModeReducer from "./slices/darkModeSlice";

/* -------------------------------------------------------------------------- */
/* 🧱 REDUX PERSIST SETUP */
/* -------------------------------------------------------------------------- */
const createNoopStorage = () => ({
  getItem: (key: string): Promise<string | null> => {
    void key;
    return Promise.resolve(null);
  },
  setItem: (key: string, value: string): Promise<string> => {
    void key;
    return Promise.resolve(value);
  },
  removeItem: (key: string): Promise<void> => {
    void key;
    return Promise.resolve();
  },
});

const storage =
  typeof window === "undefined"
    ? createNoopStorage()
    : createWebStorage("local");

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["language", "darkMode"], // 👈 only keep UI prefs persisted
};

/* -------------------------------------------------------------------------- */
/* 🧱 ROOT REDUCER */
/* -------------------------------------------------------------------------- */

const rootReducer = combineReducers({
  language: languageReducer,
  darkMode: darkModeReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

/* -------------------------------------------------------------------------- */
/* 🧱 CONFIGURE STORE */
/* -------------------------------------------------------------------------- */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          "persist/FLUSH",
          "persist/REHYDRATE",
          "persist/PAUSE",
          "persist/PERSIST",
          "persist/PURGE",
          "persist/REGISTER",
        ],
      },
    }),
});

export const persistor = persistStore(store);

/* -------------------------------------------------------------------------- */
/* 🧱 TYPES */
/* -------------------------------------------------------------------------- */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/* -------------------------------------------------------------------------- */
/* 🧱 PROVIDER COMPONENT */
/* -------------------------------------------------------------------------- */
export function StoreProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        {children}
      </PersistGate>
    </Provider>
  );
}
